import { useRef, useState } from 'react';

export interface FormErrors {
  [key: string]: string | undefined;
}

export interface FormValues {
  [key: string]: string | boolean;
}

interface UseAuthFormProps {
  initialValues: FormValues;
  validators?: {
    [key: string]: (value: any) => { isValid: boolean; error?: string };
  };
  onSubmit: (values: FormValues) => Promise<void>;
}

export function useAuthForm({ initialValues, validators = {}, onSubmit }: UseAuthFormProps) {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Garde synchrone : `isSubmitting` (state) n'est mis à jour qu'au re-render,
  // donc un double-tap rapide peut lancer onSubmit 2× (double signup/login,
  // double crédit du bonus roue). Le ref bloque immédiatement la 2e exécution.
  const submittingRef = useRef(false);

  /**
   * Update a form field value
   */
  const setFieldValue = (field: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  /**
   * Mark a field as touched and validate it
   */
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field);
  };

  /**
   * Validate a single field
   */
  const validateField = (field: string) => {
    if (!validators[field]) return true;

    const validator = validators[field];
    const result = validator(values[field]);

    if (!result.isValid) {
      setErrors((prev) => ({ ...prev, [field]: result.error }));
      return false;
    } else {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
      return true;
    }
  };

  /**
   * Validate all fields
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    Object.keys(validators).forEach((field) => {
      const validator = validators[field];
      const result = validator(values[field]);

      if (!result.isValid) {
        newErrors[field] = result.error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    
    // Mark all fields as touched
    const allTouched: { [key: string]: boolean } = {};
    Object.keys(validators).forEach((field) => {
      allTouched[field] = true;
    });
    setTouched(allTouched);

    return isValid;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    if (submittingRef.current) return;
    if (!validateForm()) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await onSubmit(values);
    } catch (error) {
      // Error handled by caller
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  /**
   * Reset form to initial state
   */
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    submittingRef.current = false;
    setIsSubmitting(false);
  };

  return {
    values,
    errors,
    touched,
    isSubmitting,
    setFieldValue,
    handleBlur,
    handleSubmit,
    resetForm,
  };
}
